import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function makeId() {
  return window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function hasConsent() {
  return localStorage.getItem('bormula_cookie_consent') === 'accepted';
}

// Consented visitors get a persistent id (recognizes return visits).
// Everyone else gets an ephemeral per-tab id that vanishes when the tab closes.
function getSessionId() {
  const store = hasConsent() ? localStorage : sessionStorage;
  let sid = store.getItem('bormula_session_id');
  if (!sid) {
    sid = makeId();
    store.setItem('bormula_session_id', sid);
  }
  return sid;
}

export function CookieConsent() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('bormula_cookie_consent')) setShowBanner(true);
  }, []);

  const handleChoice = (choice) => {
    localStorage.setItem('bormula_cookie_consent', choice);
    setShowBanner(false);
  };

  if (!showBanner || location.pathname.startsWith('/admin')) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] bg-black/95 backdrop-blur-xl border-t border-[#CCFF00]/30 px-6 py-5" data-testid="cookie-consent-banner">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-white/70 font-body">
          We collect anonymous usage stats (pages, country, device) to improve Bormula. Accept cookies to also help us recognize your return visits. No personal data is sold, ever.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => handleChoice('declined')}
            className="px-5 py-2 text-xs font-black uppercase tracking-widest text-white/50 border border-white/20 rounded-full hover:text-white hover:border-white/40 transition-colors"
            data-testid="cookie-decline-button"
          >
            Decline
          </button>
          <button
            onClick={() => handleChoice('accepted')}
            className="px-5 py-2 text-xs font-black uppercase tracking-widest bg-[#CCFF00] text-black rounded-full hover:scale-105 transition-transform"
            data-testid="cookie-accept-button"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

export function VisitorTracker({ children }) {
  const location = useLocation();

  // Page view tracking — every visitor, anonymous unless consented.
  // Module-level guard dedupes StrictMode's double-mounted effects.
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;

    const now = Date.now();
    if (window.__bormulaLastTrack && window.__bormulaLastTrack.path === location.pathname && now - window.__bormulaLastTrack.at < 2000) {
      return;
    }
    window.__bormulaLastTrack = { path: location.pathname, at: now };

    fetch(`${API}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_url: location.pathname,
        page_title: document.title,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        session_id: getSessionId(),
        consent: hasConsent(),
        screen_width: window.screen.width,
        screen_height: window.screen.height,
      }),
    }).catch(() => {});
  }, [location.pathname]);

  // Heartbeat every 60s while the page is open, so "Live now" is accurate
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;

    const beat = () => {
      if (document.visibilityState === 'hidden') return;
      fetch(`${API}/track/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId() }),
      }).catch(() => {});
    };

    const interval = setInterval(beat, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') beat(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [location.pathname]);

  return children;
}
